import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Users,
  ShoppingCart,
  FileText,
  DollarSign,
  PackageX,
  TrendingUp,
  MapPin,
  Trophy,
  Activity,
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending: '#EA5829',
  processing: '#F59E0B',
  shipped: '#3B82F6',
  delivered: '#10B981',
  completed: '#10B981',
  cancelled: '#94A3B8',
};
const FALLBACK_COLORS = ['#8FC043', '#EA5829', '#3B82F6', '#A855F7', '#F59E0B', '#0EA5E9'];

const formatTsh = (n) =>
  `Tsh ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const formatShortDate = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { headers }),
        axios.get(`${API}/dashboard/analytics`, { headers }),
      ]);
      setStats(statsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { title: 'Total Customers', value: stats?.total_customers || 0, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
    { title: 'Total Orders', value: stats?.total_orders || 0, icon: ShoppingCart, color: 'text-green-600', bg: 'bg-green-50' },
    { title: 'Pending Orders', value: stats?.pending_orders || 0, icon: TrendingUp, color: 'text-orange-600', bg: 'bg-orange-50' },
    { title: 'Total Invoices', value: stats?.total_invoices || 0, icon: FileText, color: 'text-purple-600', bg: 'bg-purple-50' },
    { title: 'Total Revenue', value: formatTsh(stats?.total_revenue), icon: DollarSign, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { title: 'Low Stock Items', value: stats?.low_stock_count || 0, icon: PackageX, color: 'text-red-600', bg: 'bg-red-50' },
  ];

  if (loading) {
    return <div className="flex justify-center items-center h-64 text-slate-500">Loading dashboard…</div>;
  }

  const topProducts = analytics?.top_products || [];
  const revenueTimeline = analytics?.revenue_timeline || [];
  const scanHeatmap = analytics?.scan_heatmap || [];
  const statusBreakdown = analytics?.status_breakdown || [];

  const heatmapMax = scanHeatmap.length ? Math.max(...scanHeatmap.map((s) => s.scan_count)) : 1;

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
        <p className="text-slate-600 mt-1">Overview of your business operations</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card
              key={stat.title}
              data-testid={`stat-card-${stat.title.toLowerCase().replace(/ /g, '-')}`}
              className="border-slate-200"
            >
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <div className={`${stat.bg} p-3 rounded-lg`}>
                    <Icon className={`h-6 w-6 ${stat.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-600">{stat.title}</p>
                    <p className="text-2xl font-bold text-slate-900 truncate">{stat.value}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Revenue Timeline */}
      <Card data-testid="revenue-chart-card">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity size={18} className="text-[#8FC043]" />
            Revenue — Last 30 Days
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueTimeline} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#8FC043" stopOpacity={0.55} />
                    <stop offset="100%" stopColor="#8FC043" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" tickFormatter={formatShortDate} fontSize={11} stroke="#64748B" />
                <YAxis fontSize={11} stroke="#64748B" tickFormatter={(v) => (v >= 1000 ? `${v / 1000}k` : v)} />
                <Tooltip
                  formatter={(v, name) =>
                    name === 'revenue' ? [formatTsh(v), 'Revenue'] : [v, 'Orders']
                  }
                  labelFormatter={(l) => new Date(l).toLocaleDateString()}
                  contentStyle={{ borderRadius: 8, borderColor: '#E2E8F0', fontSize: 12 }}
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#8FC043"
                  strokeWidth={2}
                  fill="url(#revFill)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top 5 Products */}
        <Card data-testid="top-products-chart-card">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Trophy size={18} className="text-[#EA5829]" />
              Top 5 Products by Quantity Sold
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topProducts.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-12">
                No sales data yet. Top products will appear once invoices are generated.
              </p>
            ) : (
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={topProducts}
                    layout="vertical"
                    margin={{ top: 5, right: 16, left: 8, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                    <XAxis type="number" fontSize={11} stroke="#64748B" />
                    <YAxis
                      type="category"
                      dataKey="product_name"
                      width={130}
                      fontSize={11}
                      stroke="#64748B"
                    />
                    <Tooltip
                      formatter={(v, name) =>
                        name === 'revenue' ? [formatTsh(v), 'Revenue'] : [v, 'Quantity']
                      }
                      contentStyle={{ borderRadius: 8, borderColor: '#E2E8F0', fontSize: 12 }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="quantity_sold" name="Quantity" fill="#8FC043" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Order Status Breakdown */}
        <Card data-testid="status-pie-card">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <ShoppingCart size={18} className="text-blue-600" />
              Order Status Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            {statusBreakdown.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-12">No orders yet.</p>
            ) : (
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusBreakdown}
                      dataKey="count"
                      nameKey="status"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      label={(entry) => `${entry.status} (${entry.count})`}
                      labelLine={false}
                      fontSize={11}
                    >
                      {statusBreakdown.map((entry, idx) => (
                        <Cell
                          key={entry.status}
                          fill={STATUS_COLORS[entry.status] || FALLBACK_COLORS[idx % FALLBACK_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 8, borderColor: '#E2E8F0', fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Scan Heatmap by Location */}
      <Card data-testid="scan-heatmap-card">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <MapPin size={18} className="text-[#EA5829]" />
            Customer Scans by Location
            <Badge variant="outline" className="ml-2 font-normal">
              {scanHeatmap.length} regions
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {scanHeatmap.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8">
              No customer scans recorded yet. The map populates as customers scan their carton QR codes.
            </p>
          ) : (
            <div className="space-y-2">
              {scanHeatmap.map((row) => {
                const pct = Math.round((row.scan_count / heatmapMax) * 100);
                return (
                  <div
                    key={row.location}
                    className="grid grid-cols-12 items-center gap-3 py-1"
                    data-testid={`heatmap-row-${row.location}`}
                  >
                    <div className="col-span-3 sm:col-span-2 truncate text-sm font-medium text-slate-900">
                      {row.location}
                    </div>
                    <div className="col-span-7 sm:col-span-8">
                      <div className="h-6 bg-slate-100 rounded-md overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-[#8FC043] to-[#EA5829] rounded-md transition-all"
                          style={{ width: `${Math.max(pct, 6)}%` }}
                        />
                      </div>
                    </div>
                    <div className="col-span-2 text-right text-xs text-slate-600 whitespace-nowrap">
                      <span className="font-bold text-slate-900">{row.scan_count}</span> scan
                      {row.scan_count !== 1 ? 's' : ''} · {row.unique_cartons} carton
                      {row.unique_cartons !== 1 ? 's' : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
